import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { accountsApi, type ChangeAccountStatusRequest } from "@/lib/clients-accounts";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { DetailModal } from "@/components/detail-modal";
import { toast } from "@/hooks/use-toast";

// ---------------------------------------------------------------------------
// Status transition map (Plan II 6-status set)
// ---------------------------------------------------------------------------

type Status = "Open" | "Overdue" | "FinalNotice" | "Closed" | "Hold" | "CreditHold";

const STATUS_TRANSITIONS: Record<Status, Status[]> = {
  Open:        ["Overdue", "Closed", "Hold", "CreditHold"],
  Overdue:     ["FinalNotice", "Open"],
  FinalNotice: ["Closed", "Open"],
  Hold:        ["Open", "Closed"],
  CreditHold:  ["Open", "Closed"],
  Closed:      ["Open"],
};

// ---------------------------------------------------------------------------
// Change-status drawer
// ---------------------------------------------------------------------------

export function ChangeStatusDrawer({
  open,
  onOpenChange,
  currentStatus,
  accountId,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  currentStatus: string;
  accountId: number;
}) {
  const qc = useQueryClient();
  const [newStatus, setNewStatus] = useState("");
  const [reason, setReason] = useState("");

  const validTargets: string[] =
    STATUS_TRANSITIONS[currentStatus as Status] ?? [];

  const changeMut = useMutation({
    mutationFn: (body: ChangeAccountStatusRequest) => accountsApi.status(accountId, body),
    onSuccess: () => {
      toast.success("Status changed");
      void qc.invalidateQueries({ queryKey: ["account", accountId] });
      onOpenChange(false);
      setNewStatus("");
      setReason("");
    },
    onError: () => toast.error("Failed to change status"),
  });

  return (
    <DetailModal open={open} onOpenChange={onOpenChange} title="Change account status">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (!newStatus) return;
          changeMut.mutate({ newStatusCode: newStatus, reason: reason || null });
        }}
        className="space-y-4"
      >
        <p className="text-sm text-muted-foreground">
          Current status: <strong>{currentStatus}</strong>
        </p>
        <div>
          <Label htmlFor="cs-status">New status *</Label>
          <Select value={newStatus} onValueChange={setNewStatus}>
            <SelectTrigger id="cs-status">
              <SelectValue placeholder="Select new status" />
            </SelectTrigger>
            <SelectContent>
              {validTargets.map((s) => (
                <SelectItem key={s} value={s}>{s}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {validTargets.length === 0 && (
            <p className="mt-1 text-xs text-muted-foreground">No valid transitions from current status.</p>
          )}
        </div>
        <div>
          <Label htmlFor="cs-reason">Reason (optional)</Label>
          <Input id="cs-reason" value={reason} onChange={(e) => setReason(e.target.value)} />
        </div>
        <Button type="submit" disabled={changeMut.isPending || !newStatus}>
          {changeMut.isPending ? "Changing…" : "Confirm"}
        </Button>
      </form>
    </DetailModal>
  );
}
